import { CircularProgress, makeStyles, withStyles } from '@material-ui/core'
import CheckIcon from '@material-ui/icons/Check'
import MicIcon from '@material-ui/icons/Mic'
import { authSelectors } from 'ducks/auth'
import Tooltip from 'elements/tooltip/Tooltip'
import { TransitionNone } from 'elements/TransitionNone'

import { IconButton, IconButtonProps } from 'opensolar-ui'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslate } from 'react-admin'
import { useDispatch, useSelector } from 'react-redux'
import type { OpenSolarThemeType } from 'Themes'
import { useVoice } from 'voice/OpenAIRealtimeVoiceAPI/VoiceContext'
import { useAdaActive } from './adaChat/useAdaActive'
const BigTooltip = withStyles((theme: any) => ({
  popper: {
    top: '-10px !important',
  },
  tooltip: {
    color: theme.greyDark2,
    fontSize: '12px',
    fontWeight: 400,
    backgroundColor: theme.greyLight1,
    padding: '6px 8px',
    borderRadius: '6px',
    boxShadow: theme.boxShadow.default,
    cursor: 'pointer',
    whiteSpace: 'pre-wrap',
  },

  arrow: {
    color: theme.greyLight1,
  },
}))(Tooltip)

const useStyles = makeStyles((theme: OpenSolarThemeType) => ({
  icon: {
    height: 24,
    width: 24,
    float: 'right',
    padding: 0,
    margin: 0,
    marginLeft: 0,
  },
  iconButtonRoot: {
    minWidth: 36,
    padding: 0,
    height: 36,
  },
  iconButtonPrimary: {
    color: '#cccccc',
  },
  iconButtonSuccess: {
    color: '#1751D0',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
  },
  successContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 24,
    height: 24,
    '& .success-icon': {
      animation: '$successPopAndFade 1.2s ease-out',
      color: '#1751D0',
    },
  },
  successIcon: {
    animation: '$successPopAndFade 1.2s ease-out',
    color: '#1751D0',
    fontSize: 20,
  },
  '@keyframes successPopAndFade': {
    '0%': {
      transform: 'scale(0)',
      opacity: 0,
    },
    '25%': {
      transform: 'scale(1.2)',
      opacity: 1,
    },
    '50%': {
      transform: 'scale(1)',
      opacity: 1,
    },
    '80%': {
      transform: 'scale(1)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(1)',
      opacity: 0,
    },
  },
}))

const VoiceChatButton = ({
  iconColor = 'inherit',
  disabled = false,
}: {
  iconColor?: IconButtonProps['color']
  disabled?: boolean
}) => {
  const isPro = useSelector(authSelectors.getIsAuthenticatedPro)
  const orgId = useSelector(authSelectors.getOrgId)
  const { voiceActive } = useAdaActive()

  const translate = useTranslate()
  const dispatch = useDispatch()
  const classes = useStyles()
  const { isSessionActive, isConnecting, startSession, stopSession } = useVoice()

  const [showSuccessCheck, setShowSuccessCheck] = useState(false)
  const hasShownSuccessRef = useRef(false)

  useEffect(() => {
    if (isSessionActive && !hasShownSuccessRef.current) {
      hasShownSuccessRef.current = true
      setShowSuccessCheck(true)

      const timer = setTimeout(() => {
        setShowSuccessCheck(false)
      }, 1200)

      return () => clearTimeout(timer)
    }
  }, [isSessionActive])

  useEffect(() => {
    if (!isSessionActive && !isConnecting) {
      setShowSuccessCheck(false)
      hasShownSuccessRef.current = false
    }
  }, [isSessionActive, isConnecting])

  // Listen for mic permission results from mobile app
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data)
        if (data.type === 'micPermissionResult') {
          if (data.granted) {
            // Permission granted, start the voice session
            startSession('pro', orgId).catch((error) => {
              console.error('Error starting voice session:', error)
            })
          } else {
            // Permission denied, show notification or handle appropriately
            console.warn('Microphone permission denied')
          }
        }
      } catch (error) {
        // Ignore non-JSON messages
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [startSession, orgId])

  const handleVoiceClick = useCallback(
    (e: React.MouseEvent, connectOrDisconnect: 'connect' | 'disconnect' = 'connect') => {
      e.preventDefault()

      if (connectOrDisconnect === 'connect') {
        // Request mic permission from mobile app before starting session
        if (window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage('requestMicPermission')
        } else {
          // For web browsers, start session directly
          startSession('pro', orgId).catch((error) => {
            console.error('Error starting voice session:', error)
          })
        }
      } else if (connectOrDisconnect === 'disconnect') {
        stopSession()
      }
    },
    [dispatch, startSession, stopSession, orgId]
  )

  if (!voiceActive) {
    return null
  }

  if (isConnecting) {
    return (
      <IconButton
        id="Ada-Voice-Button"
        color={iconColor}
        classes={{
          root: classes.iconButtonRoot,
        }}
        disabled={true}
      >
        <div className={classes.loadingContainer}>
          <CircularProgress size={16} thickness={4} style={{ color: '#cccccc' }} />
        </div>
      </IconButton>
    )
  }

  if (showSuccessCheck) {
    return (
      <IconButton
        color={iconColor}
        classes={{
          root: classes.iconButtonRoot,
        }}
        disabled={true}
      >
        <div className={classes.successContainer}>
          <CheckIcon className={classes.successIcon} />
        </div>
      </IconButton>
    )
  }

  return !isSessionActive ? (
    <IconButton
      id="Ada-Voice-Button"
      color={iconColor}
      classes={{
        root: classes.iconButtonRoot,
      }}
      onClick={(e) => handleVoiceClick(e, 'connect')}
      disabled={disabled}
    >
      <BigTooltip title={translate('Talk to Ada')} enterDelay={300} TransitionComponent={TransitionNone}>
        <MicIcon className={classes.icon} classes={{ root: classes.iconButtonPrimary }} />
      </BigTooltip>
    </IconButton>
  ) : (
    <IconButton
      id="Ada-Voice-Button"
      classes={{
        root: classes.iconButtonRoot,
      }}
      onClick={(e) => handleVoiceClick(e, 'disconnect')}
      disabled={disabled}
    >
      <BigTooltip title={translate('Disconnect')} enterDelay={300} TransitionComponent={TransitionNone}>
        <MicIcon className={classes.icon} classes={{ root: classes.iconButtonSuccess }} />
      </BigTooltip>
    </IconButton>
  )
}

export default VoiceChatButton
